import { DecagonMetadata } from './Decagon.interface';

import config, { isProduction } from 'modules/common/config';
import { localViews } from 'modules/Auth/AuthProvider/views';
import { formatFirstPaymentDate } from 'modules/common/display';
import { Member } from 'modules/common/interfaces/member';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const US_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * Normalises a member date to ISO 8601, which is what every date in Decagon metadata uses.
 *
 * The member payload serializes `establisheddate` as MM/DD/YYYY (the backend's
 * EstablishedDateField bridges the legacy string API), while the nested business dates are
 * already ISO. Converted by pattern rather than by parsing to a Date, because a date-only value
 * names a calendar day and would shift for viewers west of UTC. Anything unrecognised, unset or
 * null becomes '' (the same "no value" signal the existing keys use).
 */
function toIsoDate(value?: string | null): string {
    if (!value) {
        return '';
    }
    if (ISO_DATE_PATTERN.test(value)) {
        return value;
    }
    const match = US_DATE_PATTERN.exec(value);

    return match ? `${match[3]}-${match[1]}-${match[2]}` : '';
}

/**
 * The eight business-identity scalars behind the `decagon_business_context_metadata` gate
 * (HUB-2023), preloaded so the identity AOPs (Form 2553 Correction, Scorp Election Date, Update
 * Name, Find NAICS Code) can answer without a tool call.
 *
 * Everything here is already on the member in the store, so this costs no fetch. Deliberately
 * excluded and left behind the `get_business_context` tool: EIN, registered agent, shareholders
 * and street lines. Mirrors the backend's `_enrich_metadata` (hyke/ai/decagon/service.py), which
 * has to build the same map for agent assist until HUB-1909 unifies the two init paths.
 */
export const BUSINESS_CONTEXT_KEYS = [
    'legalBusinessName',
    'federalBusinessName',
    'entityType',
    'stateOfIncorporation',
    'entityNumber',
    'establishedDate',
    'mailingSameAsBusiness',
    'preferredSElectionDate',
] as const;

export function getBusinessContextMetadata(member: Member): Partial<DecagonMetadata> {
    return {
        // Business.name drops the incorporation suffix into its own column, so `businessname`
        // (already sent as businessName) is the bare name and this is the name as filed.
        legalBusinessName: member.full_business_name ?? '',
        federalBusinessName: member.federal_business_name ?? '',
        entityType: member.incorporation ?? '',
        // Region's primary key is the two-letter code, so the id is the value itself.
        stateOfIncorporation: member.business?.state_of_inc?.id ?? '',
        entityNumber: member.entitynumber ?? '',
        establishedDate: toIsoDate(member.establisheddate),
        // A null mailing_address means "same as the business address" on the wire.
        mailingSameAsBusiness: !member.mailing_address,
        preferredSElectionDate: member.business?.preferred_s_election_date ?? '',
    };
}

/**
 * The API token Decagon may hold. While impersonating it is the member's own credential minted for
 * a staffer, so Decagon gets none: its tools must not act as the member on a staffer's behalf.
 */
export function decagonTokenFor(accessToken: string, isImpersonating?: boolean): string {
    return isImpersonating || localViews.isImpersonating() ? '' : accessToken;
}

export function getMemberMetadata(
    member: Member,
    accessToken: string,
    isImpersonating?: boolean,
    currentPage?: string,
    isBusinessContextEnabled?: boolean,
    isPayrollDashboardV2?: boolean,
    isPayrollDashboardV2ShowLegacy?: boolean
): DecagonMetadata {
    return {
        memberId: member.id,
        firstName: member.first_name,
        lastName: member.last_name,
        preferredName: member.preferred_name,
        preferredFirstName: member.preferred_name ? member.preferred_name.split(' ')[0] : member.first_name,
        fullName: member.fullname,
        scorpElectionDate: member.business.s_corp_election_date ?? '',
        memberFirstPaymentDate: formatFirstPaymentDate(member.firstpayment),
        email: member.email,
        businessName: member.businessname,
        businessAddressState: member.business_address?.state ?? '',
        stateOfOperation: member.stateofoperation,
        userType: member.usertype,
        membershipTier: member.membership_tier,
        bookkeepingProvider: member.bookkeeping_provider,
        token: decagonTokenFor(accessToken, isImpersonating),
        environment: config.environment,
        memberStage: member.stage ?? 'unknown',
        // Raw UA rather than a device bucket: classification lives in dbt
        // (fct_decagon_conversations), so it can be corrected across all history
        // instead of being frozen at whatever regex shipped (CA-5704).
        browser_user_agent: navigator.userAgent,
        ...(currentPage !== undefined && { currentPage }),
        ...(!isProduction() && { _decagon_branch_name: 'main' }),
        ...(isImpersonating !== undefined && { isImpersonating }),
        ...(isPayrollDashboardV2 !== undefined && { payrollDashboardV2: isPayrollDashboardV2 }),
        ...(isPayrollDashboardV2ShowLegacy !== undefined && {
            payrollDashboardV2ShowLegacy: isPayrollDashboardV2ShowLegacy,
        }),
        ...(isBusinessContextEnabled ? getBusinessContextMetadata(member) : {}),
    };
}
